<template>
  <div class="known-business-entity-contact-component">
    <div class="flex items-center justify-between">
      <div>
        <span v-if="modelValue.knownBusinessEntity" class="sr-only block text-sm">Bedrijf</span>
        <span v-else class="sr-only block text-sm">Particulier</span>
      </div>
    </div>
    <div
      class="border-surface gray-lighten-2 relative rounded-md border-2 p-2"
      :class="{
        'opacity-50': isBeingDeleted || !modelValue.isActive
      }"
    >
      <div class="space-y-2">
        <div class="mb-2 flex items-center justify-between">
          <div class="flex flex-col">
            <!-- Has an associated business: whole block opens the business entity edit form -->
            <ModelEditLink
              v-if="modelValue.knownBusinessEntity"
              :model="modelValue.knownBusinessEntity"
              class="flex flex-col"
            >
              <div class="text-sm font-medium">
                <KnownBusinessEntityContactCardWrapper :known-business-entity-contact="modelValue">
                  {{ modelValue.knownBusinessEntity?.name }}
                </KnownBusinessEntityContactCardWrapper>
              </div>

              <div class="flex flex-col text-xs">
                <!-- RegisteredBusinessEntity -->
                <div
                  v-if="modelValue.knownBusinessEntity.registeredBusinessEntity"
                  class="space-x-1"
                >
                  <span>{{
                    modelValue.knownBusinessEntity?.registeredBusinessEntity.getMainBusinessLocation()
                      .location.address
                  }}</span>
                  <span>KVK-nr. {{ modelValue.knownBusinessEntity?.getRegistrationNumber() }}</span>
                </div>

                <!-- Custom defined business -->
                <div v-else class="flex flex-col text-xs">
                  <span
                    >Land: {{ modelValue.knownBusinessEntity?.getCountryOfRegistrationString() }}
                  </span>
                  <span
                    >Registratienr.: {{ modelValue.knownBusinessEntity?.getRegistrationNumber() }}
                  </span>
                </div>
              </div>
            </ModelEditLink>

            <!-- No associated business -->
            <template v-else>
              <div class="text-sm font-medium">
                <KnownBusinessEntityContactCardWrapper :known-business-entity-contact="modelValue">
                  {{ modelValue.person.fullName }}
                </KnownBusinessEntityContactCardWrapper>
              </div>

              <!-- No associated business = private individual -->
              <div v-if="modelValue.isPrivateIndividual">Particulier</div>

              <!-- Else, something must be wrong (should never happen) -->
              <div v-else>Onbekend</div>
            </template>
          </div>
        </div>
        <div class="space-y-2">
          <ul class="space-y-2">
            <li>
              <label>
                <span class="sr-only block text-sm">E-mailadres</span>
                <InputText
                  v-model="modelValue.email"
                  placeholder="E-mailadres"
                  :errors="v$.email.$errors"
                  :disabled="!modelValue.isActive"
                  @input="onInput"
                  @blur="v$.email.$touch()"
                  type="email"
                  ref="emailInput"
                />
              </label>
            </li>
            <li>
              <label>
                <span class="sr-only block text-sm">Telefoonnummer</span>
                <InputPhoneNumber
                  v-model="modelValue.phoneNumber"
                  @input="onInput"
                  placeholder="Telefoonnummer"
                  :disabled="!modelValue.isActive"
                  class="pl-24"
                  :errors="v$.phoneNumber.$errors"
                  @blur="v$.phoneNumber.$touch()"
                />
              </label>
            </li>
            <slot name="extend-form" :disabled="!modelValue.isActive" />
          </ul>

          <div
            class="px-1 text-xs select-none"
            v-if="modelValue.userId && modelValue.userSignupDetails"
          >
            Geregistreerd met:

            <template v-if="modelValue.userSignupDetails.email">
              {{ modelValue.userSignupDetails.email }}
            </template>
            <template v-if="modelValue.userSignupDetails.phoneNumber">
              {{ modelValue.userSignupDetails.phoneNumber }}
            </template>
          </div>

          <KnownBusinessEntityContactRoleList
            v-model="modelValue.roles"
            :disabled="!modelValue.isActive"
            :known-business-entity-contact="modelValue"
            @role-changed="$emit('kbecChanged', $event)"
            class="mt-2"
          />

          <div v-if="!isPrivateIndividual && modelValue.roles.length === 0" class="mt-2 text-xs">
            <strong>Let op:</strong>
            {{ modelValue.person.fullName }} heeft nog geen rol bij
            {{ modelValue.knownBusinessEntity?.name }}.
          </div>

          <div class="mt-2">
            <GeneralButton
              v-if="!isAddingRole && potentialRoles.length > 0"
              @click.stop="isAddingRole = true"
              size="small"
              :disabled="!modelValue.isActive"
              variant="text"
              ref="addRoleButton"
              :icon="['fas', 'plus']"
            >
              Rol toevoegen
            </GeneralButton>
          </div>

          <div
            v-if="isAddingRole && modelValue.isActive"
            class="flex w-full flex-col"
            :class="isSaving ? 'opacity-50' : ''"
          >
            <span class="sr-only block text-sm">Rol</span>
            <div class="border-surface rounded-md border-2 p-2">
              <div @click.stop class="mt-1">
                <!-- @vue-ignore -->
                <InputCombobox
                  v-model="selectedRoleType"
                  :items="potentialRoles"
                  items-key-name="key"
                  items-value-name="value"
                  class="flex-1"
                  input-placeholder="Rol"
                  auto-focus
                  show-options-on-focus
                  @item-selected="onRoleTypeSelected"
                />
              </div>
              <div class="mt-2">
                <GeneralButton class="border-gray border" @click.stop="isAddingRole = false">
                  Annuleren
                </GeneralButton>
              </div>
            </div>
          </div>

          <div
            v-if="
              !modelValue.userId &&
              modelValue.invitationCode &&
              (isPrivateIndividual ||
                modelValue.roles.some((role) => role.type === RoleTypes.SERVICE_PROVIDER))
            "
            class="mt-2"
          >
            <KnownBusinessEntityContactInviteCodeButton
              :invitation-code="modelValue.invitationCode"
              :recipient-name="modelValue.person.firstName"
            />
          </div>

          <AutoUpdateStatus
            :is-updating="isSaving"
            :error-encountered="errorEncountered"
            :time-hide-after-success="2000"
          />
        </div>
      </div>
      <div class="absolute top-2 right-2 flex items-center justify-center gap-x-2">
        <span class="text-sm">Actief</span>
        <InputCheckbox
          tabindex="-1"
          v-model="modelValue.isActive"
          @update:model-value="updateKnownBusinessEntityContact"
        />
      </div>
    </div>
    <div>
      <GeneralButton
        v-if="!modelValue.isActive"
        @click.stop="deleteKnownBusinessEntityContact"
        class="hover:text-error mb-1"
        tabindex="-1"
        size="small"
        variant="text"
        color="surface"
        :icon="['fas', 'trash-can']"
      >
        <template v-if="modelValue.knownBusinessEntity"> Bedrijf loskoppelen van persoon </template>
        <template v-else> Particuliere status loskoppelen </template>
      </GeneralButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, ref, type Ref } from "vue"
import type { KnownBusinessEntityContact } from "@/models/known-business-entity-contact.model.ts"
import { DialogTypes } from "@/interfaces/component/dialog.interface"
import { useDialogStore } from "@/store/dialog.store"
import { RoleTypes } from "@/enums/role-types"
import { getSubclassForRoleType } from "@/helper/role-helper"
import { useRoleStore } from "@/store/roles.store"
import ModelManager from "@/services/model-manager"
import useDelayedFunction from "@/composables/input/delayed-function"
import { emailValidator, phoneNumberValidator } from "@/helper/validation/validators"
import useVuelidate from "@vuelidate/core"
import { KnownBusinessEntityContactController } from "@/controllers/model-controllers/known-business-entity-contact-controller"
import GeneralButton from "@/components/inputs/GeneralButton.vue"
import KnownBusinessEntityContactRoleList from "@/components/known_business_entity_contact/KnownBusinessEntityContactRoleList.vue"
import AutoUpdateStatus from "@/components/instruction/AutoUpdateStatus.vue"
import InputPhoneNumber from "@/components/inputs/InputPhoneNumber.vue"
import KnownBusinessEntityContactCardWrapper from "@/components/known_business_entity_contact/KnownBusinessEntityContactCardWrapper.vue"
import InputText from "@/components/inputs/InputText.vue"
import InputCombobox from "@/components/inputs/input_combobox/InputCombobox.vue"
import { useKnownBusinessEntitiesContactsStore } from "@/store/known-business-entities-contacts.store.ts"
import { storeToRefs } from "pinia"
import { PersonController } from "@/controllers/model-controllers/person-controller"
import KnownBusinessEntityContactInviteCodeButton from "@/components/known_business_entity_contact/KnownBusinessEntityContactInviteCodeButton.vue"
import {
  KBECEmailIsUniqueValidator,
  personPhoneNumberIsUniqueValidator
} from "@/helper/validation/known-business-entity-contact-validation"
import { type ModelConstructorSignature } from "@/models/types/model-constructor-signature"
import InputCheckbox from "@/components/inputs/InputCheckbox.vue"
import ModelEditLink from "@/components/misc/ModelEditLink.vue"
import countries from "i18n-iso-countries"
import nl from "i18n-iso-countries/langs/nl.json"

countries.registerLocale(nl)

const emit = defineEmits(["update:modelValue", "kbecChanged", "addressCleared"])

const modelValue = defineModel<KnownBusinessEntityContact>({
  required: true
})

const roleTypeMap: Record<RoleTypes, string> = {
  [RoleTypes.PLANNER]: "Planner",
  [RoleTypes.SERVICE_PROVIDER]: "Dienstverlener",
  [RoleTypes.FINANCIAL_OFFICER]: "Financieel verantwoordelijke"
}

const roleStore = useRoleStore()
const dialogStore = useDialogStore()

const isAddingRole = ref(false)
const isSaving = ref(false)
const isBeingDeleted = ref(false)

const errorEncountered = ref(false)

const selectedRoleType: Ref<RoleTypes | null | undefined> = ref(null)

const knownBusinessEntityContactController: KnownBusinessEntityContactController = inject(
  "knownBusinessEntityContactController"
)!
const delayedUpdateKBEC = useDelayedFunction(updateKnownBusinessEntityContact)

async function updateKnownBusinessEntityContact() {
  if (!isValidUpdate()) {
    return
  }

  isSaving.value = true
  try {
    await knownBusinessEntityContactController.update(modelValue.value)
    isSaving.value = false
  } catch (e) {
    console.log(e)
    errorEncountered.value = true
  }
}

const potentialRoles = computed(() => {
  const disabledRoleTypes = [RoleTypes.PLANNER.toString()]
  const existingRoleTypes = modelValue.value.roles.map((role) => role.type.toString())

  return Object.entries(roleTypeMap)
    .filter(([key]) => !existingRoleTypes.includes(key) && !disabledRoleTypes.includes(key))
    .map(([key, value]) => ({ key, value }))
})

const isPrivateIndividual = computed(() => !modelValue.value.knownBusinessEntity)

const knownBusinessEntityContactStore = useKnownBusinessEntitiesContactsStore()
const { knownBusinessEntitiesContacts } = storeToRefs(knownBusinessEntityContactStore)
const personController: PersonController = inject("personController")!
function deleteKnownBusinessEntityContact() {
  const name = isPrivateIndividual.value
    ? "de particuliere status"
    : modelValue.value.knownBusinessEntity?.name

  dialogStore.currentDialog = {
    title: "Bedrijfspersoon loskoppelen",
    body: `Weet je zeker dat je ${name} wilt loskoppelen van ${modelValue.value.person.fullName}?`,
    type: DialogTypes.CONFIRM,
    onConfirm: async () => {
      isBeingDeleted.value = true

      const remainingKnownBusinessEntityContactsForPerson =
        knownBusinessEntitiesContacts.value.filter(
          (kbec) => kbec.id !== modelValue.value.id && kbec.person.id === modelValue.value.person.id
        )

      if (isPrivateIndividual.value) {
        modelValue.value.person.address.clear()
        await personController.update(modelValue.value.person)
        emit("addressCleared")
      }

      if (remainingKnownBusinessEntityContactsForPerson.length === 0) {
        modelValue.value.knownBusinessEntity = null
        modelValue.value.isPrivateIndividual = false
        await knownBusinessEntityContactController.update(modelValue.value)
      } else {
        await knownBusinessEntityContactController.delete(modelValue.value)
      }
      isBeingDeleted.value = false
    },
    textConfirm: "Ja, verwijderen",
    classConfirm: "bg-error! text-on-error!"
  }
}

async function onRoleTypeSelected(entry: { key: RoleTypes; value: string }) {
  isSaving.value = true

  const concreteRoleClass = getSubclassForRoleType(entry.key) as ModelConstructorSignature<any, any>
  const role = ModelManager.getInstance().create(concreteRoleClass)

  await roleStore.saveRole(role, modelValue.value)

  selectedRoleType.value = null
  isAddingRole.value = false

  isSaving.value = false

  emit("kbecChanged", modelValue.value) // Will be used to store "lastModifiedKnownBusinessEntityContact" in parent.
}

async function onInput() {
  if (!isValidUpdate()) {
    return
  }

  errorEncountered.value = false
  await delayedUpdateKBEC.schedule()
}

function isValidUpdate() {
  if (isBeingDeleted.value) {
    return false
  }

  if (v$.value.$invalid) {
    errorEncountered.value = true
    return false
  }
  return true
}

const addRoleButton = ref()
function focusRoleButton() {
  if (addRoleButton.value) {
    // Could be absent if it is a private individual.
    addRoleButton.value.focus()
  }
}

const emailInput = ref()
function focusEmailInput() {
  if (emailInput.value) {
    emailInput.value.focus()
  }
}

defineExpose({
  focusRoleButton,
  focusEmailInput
})

const rules = {
  email: { emailValidator, KBECEmailIsUniqueValidator },
  phoneNumber: {
    phoneNumberValidator,
    KBECPhoneNumberIsUniqueValidator: personPhoneNumberIsUniqueValidator
  }
}

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error
const v$ = useVuelidate(rules, modelValue.value)
</script>

<style scoped></style>
